import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useBulkEmailChildren } from "@/admin/hooks/use-claims";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Textarea } from "@/shared/components/ui/textarea";
import { toastApiError } from "@/shared/lib/api-error";

/**
 * Sends the same customer-visible message to every unit claim's consumer
 * under an HOA parent claim. Mirrors ContactCustomerDialog but fans out
 * to all children via POST /claims/claims/<uuid>/bulk-email/.
 */
export function BulkEmailDialog({
  open,
  onOpenChange,
  parentUuid,
  recipientCount,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentUuid: string;
  recipientCount: number;
}) {
  const [message, setMessage] = useState("");
  const bulkEmail = useBulkEmailChildren();

  const handleSend = async () => {
    if (message.trim().length < 10) {
      toast.error("Message must be at least 10 characters");
      return;
    }

    try {
      const result = await bulkEmail.mutateAsync({
        parentUuid,
        message: message.trim(),
      });
      const sent = result?.sent_count ?? 0;
      const skipped = result?.skipped_count ?? 0;
      toast.success(
        skipped > 0
          ? `Sent to ${sent} contact${sent !== 1 ? "s" : ""} (${skipped} skipped — no email)`
          : `Sent to ${sent} contact${sent !== 1 ? "s" : ""}`,
      );
      setMessage("");
      onOpenChange(false);
    } catch (err: unknown) {
      toastApiError(err, "Failed to send bulk message");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
            Email Unit Contacts
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            Send the same message to the homeowner on every unit claim in this
            HOA ({recipientCount} contact{recipientCount !== 1 ? "s" : ""}). It
            appears in each unit&apos;s claim history and is emailed to the
            customer.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-3">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write the update you want every unit homeowner to receive..."
            rows={5}
            className="w-full resize-y rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-3 text-[13px] leading-[1.6] text-[var(--ink)] outline-none transition-colors focus:border-[var(--green)]"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={bulkEmail.isPending || message.trim().length < 10}
            className="cursor-pointer"
          >
            {bulkEmail.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            )}
            Send to {recipientCount} contact{recipientCount !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
